/**
 * A compact, non-looping scroll-wheel time picker for the automation delivery-time prefs
 * (Daily Brief, Overnight Processing). Two wheels — an HOUR wheel (24 rows, labelled 12-hour with a
 * meridiem so it reads naturally yet stays one wheel) and a 15-MINUTE wheel (00/15/30/45). Both are
 * clamped (spacer-padded), so they never loop/wrap past the ends — unlike the native `<input
 * type="time">` they replace. Scroll OR tap a row to pick; the centred row is the selection.
 *
 * Value in/out is the schema's canonical "HH:MM" 24-hour string (see @xavior/schema deliveryTime).
 */
import { useCallback, useEffect, useRef, type ReactNode } from "react";

const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const MINUTE_OPTIONS = [0, 15, 30, 45] as const;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);
const ITEM_H = 34; // px per row; keep in sync with .timewheel CSS

/** Snap a raw minute to the nearest 15-minute interval, clamped to 0..45 (never 60). */
export function snapTo15(minute: number): number {
  if (minute <= 0) return 0;
  return Math.min(45, Math.round(minute / 15) * 15);
}

/** Parse "HH:MM" (24h) into hour + 15-snapped minute; default 09:00 for missing/invalid input. */
export function parseDeliveryTime(value: string | undefined): { hour24: number; minute: number } {
  const m = value ? TIME_RE.exec(value.trim()) : null;
  if (!m) return { hour24: 9, minute: 0 };
  return { hour24: Number(m[1]), minute: snapTo15(Number(m[2])) };
}

/** Format a 24h hour + minute back to the canonical zero-padded "HH:MM". */
export function formatDeliveryTime(hour24: number, minute: number): string {
  return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** A 24h hour as a 12-hour label with meridiem: 0→"12 AM", 13→"1 PM", 23→"11 PM". */
export function hour12Label(hour24: number): string {
  const meridiem = hour24 < 12 ? "AM" : "PM";
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${h12} ${meridiem}`;
}

/** One clamped (non-looping) scroll-snap column. The centred row is selected. */
function Wheel<T extends number>({
  ariaLabel,
  options,
  selected,
  renderLabel,
  optionTestId,
  onSelect,
}: {
  ariaLabel: string;
  options: readonly T[];
  selected: T;
  renderLabel: (value: T) => ReactNode;
  optionTestId?: string;
  onSelect: (value: T) => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const programmaticRef = useRef(false);
  const programmaticTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const indexOfSelected = Math.max(0, options.indexOf(selected));

  // Centre the selected row whenever it changes (and on mount). Guarded so the resulting scroll
  // event doesn't bounce back into onSelect. No-op in environments without scrollTo (jsdom/happy-dom).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    programmaticRef.current = true;
    if (programmaticTimer.current) clearTimeout(programmaticTimer.current);
    programmaticTimer.current = setTimeout(() => {
      programmaticRef.current = false;
    }, 200);
    el.scrollTo?.({ top: indexOfSelected * ITEM_H, behavior: "auto" });
  }, [indexOfSelected]);

  const onScroll = useCallback(() => {
    if (programmaticRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    // Debounce to the rest position, then snap the centred row to the value.
    scrollTimer.current = setTimeout(() => {
      const idx = Math.max(0, Math.min(options.length - 1, Math.round(el.scrollTop / ITEM_H)));
      const next = options[idx];
      if (next !== selectedRef.current) onSelect(next); // loop-breaker: only commit a real change
    }, 110);
  }, [options, onSelect]);

  useEffect(
    () => () => {
      if (programmaticTimer.current) clearTimeout(programmaticTimer.current);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
    },
    [],
  );

  return (
    <div
      ref={scrollRef}
      className="timewheel__col xv-scroll"
      role="listbox"
      aria-label={ariaLabel}
      tabIndex={0}
      onScroll={onScroll}
    >
      <div className="timewheel__pad" aria-hidden="true" />
      {options.map((opt) => {
        const isSel = opt === selected;
        return (
          <div
            key={opt}
            role="option"
            aria-selected={isSel}
            data-testid={optionTestId}
            className={`timewheel__opt${isSel ? " is-selected" : ""}`}
            onClick={() => onSelect(opt)}
          >
            {renderLabel(opt)}
          </div>
        );
      })}
      <div className="timewheel__pad" aria-hidden="true" />
    </div>
  );
}

export function TimeWheel({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const { hour24, minute } = parseDeliveryTime(value);
  return (
    <div className="prefs__field">
      <span className="prefs__label" id={`${id}-label`}>
        {label}
      </span>
      <div className="timewheel" role="group" aria-labelledby={`${id}-label`}>
        <div className="timewheel__band" aria-hidden="true" />
        <Wheel
          ariaLabel="Hour"
          options={HOUR_OPTIONS}
          selected={hour24}
          renderLabel={(h) => hour12Label(h)}
          onSelect={(h) => onChange(formatDeliveryTime(h, minute))}
        />
        <span className="timewheel__sep" aria-hidden="true">
          :
        </span>
        <Wheel
          ariaLabel="Minute"
          options={MINUTE_OPTIONS}
          selected={minute}
          optionTestId="timewheel-minute-option"
          renderLabel={(m) => String(m).padStart(2, "0")}
          onSelect={(m) => onChange(formatDeliveryTime(hour24, m))}
        />
      </div>
    </div>
  );
}
